/**
 * @jsxRuntime classic
 * @jsx jsx
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';

import type {
	Command,
	FloatingToolbarConfig,
	FloatingToolbarHandler,
} from '@atlaskit/editor-common/types';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { akEditorFloatingPanelZIndex } from '@atlaskit/editor-shared-styles';
import CoreCloseIcon from '@atlaskit/icon/core/close';
import CoreMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import CoreThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import CoreThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { token } from '@atlaskit/tokens';
import { LogoSvg } from '@atlassian/generative-ai-modal/assets/Logo';

import { highlightSentence, unhighlightSentence } from '../pm-plugins/ai-spelling-grammar/commands';
import {
	insertSuggestionWithAnalytics,
	removeSuggestionWithAnalytics,
} from '../pm-plugins/ai-spelling-grammar/commands-with-analytics';
import { getPluginState as getSpellingAndGrammarPluginState } from '../pm-plugins/ai-spelling-grammar/plugin-factory';
import {
	getSelectedDiffObject,
	getSentenceFromDiffObject,
} from '../pm-plugins/ai-spelling-grammar/utils';
import { createOpenAIModalCommand } from '../pm-plugins/decoration/actions';
import { getPluginState as getDecorationPluginState } from '../pm-plugins/decoration/decoration-plugin-factory';
import { improveWriting } from '../prebuilt/config-items-range/enhance/enhance';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../types';

import { messages } from './messages';
import { separator } from './seperator';

type IconProps = { label: string; iconColor?: string };

const CloseIcon = ({ label }: IconProps) => (
	<CoreCloseIcon label={label} color={token('color.icon', '#44546F')} spacing="spacious" />
);
const MoreIcon = ({ label }: IconProps) => (
	<CoreMoreIcon label={label} spacing="spacious" color="currentColor" />
);

const ImproveSentenceIcon = ({ label }: IconProps) => {
	return <LogoSvg alt={label} />;
};

export const getFloatingToolbarConfig =
	({
		aiGlobalOptIn,
		editorPluginAIProvider,
	}: {
		aiGlobalOptIn: AIGlobalOptIn;
		editorPluginAIProvider: EditorPluginAIProvider;
	}): FloatingToolbarHandler =>
	(state, intl) => {
		const pluginState = getSpellingAndGrammarPluginState(state) || {};
		const { isSpellingAndGrammarEnabled, selectedID } = pluginState;

		const { product, handleFeedbackSubmission } = editorPluginAIProvider;

		if (isSpellingAndGrammarEnabled && state.selection.from === state.selection.to) {
			const decorationPluginState = getDecorationPluginState(state);
			if (decorationPluginState.modalDecorationSet !== DecorationSet.empty) {
				return;
			}

			const selectedDiff = getSelectedDiffObject(pluginState, state.selection.to);

			if (!selectedDiff || !selectedID) {
				return;
			}

			const sentence = getSentenceFromDiffObject(pluginState, selectedDiff);
			const sentenceItems: FloatingToolbarConfig['items'] = [];

			if (sentence) {
				const openAIModalAndUnhighlightSentenceCommand: Command = (state, dispatch) =>
					createOpenAIModalCommand({
						state,
						configItem: improveWriting,
						aiGlobalOptIn,
						positions: {
							startPos: sentence.from,
							endPos: sentence.to,
						},
						lastTriggeredFrom: 'proactiveAIFloatingToolbar',
					})(state, dispatch) && // If modal opens, remove the hover sentence highlight
					unhighlightSentence()(state, dispatch);

				sentenceItems.push(separator(), {
					id: 'editor.ai.sg-improve-sentence',
					type: 'button',
					testId: 'ak-editor-ai-suggestion-toolbar-improve-sentence-button',
					icon: ImproveSentenceIcon,
					onClick: openAIModalAndUnhighlightSentenceCommand,
					onMouseEnter: highlightSentence(sentence),
					onMouseLeave: unhighlightSentence(),
					onFocus: highlightSentence(sentence),
					onBlur: unhighlightSentence(),
					title: intl.formatMessage(messages.improveSentenceToolbarButtonLabel),
					showTitle: true,
				});
			}

			const dropdownOptions = [
				{
					title: intl.formatMessage(messages.correctSuggestion),
					icon: (
						<CoreThumbsUpIcon
							label="thumbsupIcon"
							spacing="spacious"
							color={token('color.icon', '#44546F')}
						/>
					),
					onClick: (): boolean => {
						if (!handleFeedbackSubmission || !sentence) {
							return false;
						}
						handleFeedbackSubmission({
							sentiment: 'good-suggestion',
							getAIExperience: (hasUserConsent?: boolean) => ({
								...(hasUserConsent
									? { originalText, suggestionText, selectedSentence: sentence.text }
									: {}),
							}),
							editorAttributes: { product },
						});
						return true;
					},
				},
				{
					title: intl.formatMessage(messages.incorrectSuggestion),
					icon: (
						<CoreThumbsDownIcon
							label="thumbsdownIcon"
							spacing="spacious"
							color={token('color.icon', '#44546F')}
						/>
					),
					onClick: (): boolean => {
						if (!handleFeedbackSubmission || !sentence) {
							return false;
						}
						handleFeedbackSubmission({
							sentiment: 'bad-suggestion',
							getAIExperience: (hasUserConsent?: boolean) => ({
								...(hasUserConsent
									? { originalText, suggestionText, selectedSentence: sentence.text }
									: {}),
							}),
							editorAttributes: { product },
						});
						return true;
					},
				},
			];

			const nodeType = [
				state.schema.nodes.paragraph,
				state.schema.nodes.text,
				state.schema.nodes.heading,
				state.schema.nodes.taskItem,
				state.schema.nodes.decisionItem,
			];

			const { from, to, text: suggestionText, originalText } = selectedDiff;

			const items: FloatingToolbarConfig['items'] = [
				{
					id: 'editor.ai.sg-suggestion',
					type: 'button',
					showTitle: true,
					testId: 'ak-editor-ai-suggestion-toolbar-suggestion-button',
					onClick: insertSuggestionWithAnalytics(from, to, selectedID),
					title: suggestionText,
					focusEditoronEnter: true,
				},
				...sentenceItems,
				separator(),
				{
					id: 'editor.ai.sg-dismiss',
					type: 'button',
					testId: 'ak-editor-ai-suggestion-toolbar-dismiss-button',
					icon: CloseIcon,
					onClick: removeSuggestionWithAnalytics(originalText, selectedID),
					title: intl.formatMessage(messages.dismissSuggestion),
					focusEditoronEnter: true,
				},
				separator(),
				{
					type: 'dropdown',
					title: 'Spelling & Grammar dropdown',
					id: 'editor.ai.sg-dropdown',
					icon: MoreIcon,
					options: dropdownOptions,
					hideExpandIcon: true,
					alignDropdownWithToolbar: false,
					dropdownWidth: 180, // Width of the dropdown at the moment hardcoded to 180px
				},
			];

			return {
				title: 'Spelling & Grammar controls',
				getDomRef: (editorView) =>
					findDomRefAtPos(from, editorView.domAtPos.bind(editorView)) as HTMLElement,
				nodeType,
				zIndex: akEditorFloatingPanelZIndex + 1, // Place the context menu slightly above the others
				items,
				scrollable: false,
				align: 'left',
			};
		}
		return;
	};
